import { CSSProperties, useEffect, useState } from 'react';
import { ENGINE_HTTP_URL, PUBLIC_API_URL } from 'utils/urls';

type Cfg = Record<string, unknown>;
type Result = { config?: Cfg; error?: string };

// Each source exposes its own public, non-sensitive /public-config allowlist.
// Showing both lets you confirm the serverless side matches the engine (they are
// driven by one deploy toggle) and spot any divergence.
const SOURCES = [
  { key: 'engine', label: 'Engine (Socket.IO)', url: `${ENGINE_HTTP_URL}/public-config` },
  { key: 'serverless', label: 'Serverless (API Gateway)', url: `${PUBLIC_API_URL}/public-config` }
];

/**
 * Unlisted diagnostics page ("/config") showing each backend's public,
 * non-sensitive config (the server-enforced /public-config allowlists). Not
 * linked from any navigation — reachable only by knowing the URL — and never
 * exposes secrets.
 */
const ConfigPage = () => {
  const [results, setResults] = useState<Record<string, Result>>({});

  useEffect(() => {
    let cancelled = false;
    SOURCES.forEach(({ key, url }) => {
      fetch(url)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((config) => {
          if (!cancelled) setResults((prev) => ({ ...prev, [key]: { config } }));
        })
        .catch((err) => {
          if (!cancelled)
            setResults((prev) => ({ ...prev, [key]: { error: err?.message || 'Failed to load' } }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const renderValue = (key: string, value: unknown) => {
    if (typeof value === 'boolean') {
      const danger = key.toLowerCase().includes('hammer') && value;
      return <span style={danger ? boolOnDanger : value ? boolOn : boolOff}>{String(value)}</span>;
    }
    if (value === null || value === undefined || value === '') {
      return <span style={emptyVal}>—</span>;
    }
    return <span style={strVal}>{String(value)}</span>;
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={titleStyle}>Server Configuration</h1>
        <p style={subtitleStyle}>
          Public, non-sensitive runtime config from each backend. Secrets are never exposed here.
        </p>

        {SOURCES.map(({ key, label }) => {
          const result = results[key];
          return (
            <div key={key} style={sectionStyle}>
              <h2 style={sectionTitleStyle}>{label}</h2>
              {!result && <div style={loadingStyle}>Loading…</div>}
              {result?.error && <div style={errorStyle}>Could not load: {result.error}</div>}
              {result?.config && (
                <table style={tableStyle}>
                  <tbody>
                    {Object.entries(result.config).map(([k, v]) => (
                      <tr key={k} style={rowStyle}>
                        <td style={keyCell}>{k}</td>
                        <td style={valCell}>{renderValue(k, v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const pageStyle: CSSProperties = {
  minHeight: '100vh',
  background: '#0f1923',
  color: '#e6edf3',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '2rem 1rem',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
};

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 560,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 12,
  padding: '1.5rem'
};

const titleStyle: CSSProperties = { margin: '0 0 0.25rem', fontSize: '1.25rem', color: '#f0c97e' };
const subtitleStyle: CSSProperties = {
  margin: '0 0 1.25rem',
  fontSize: '0.8rem',
  color: 'rgba(230, 237, 243, 0.55)'
};
const sectionStyle: CSSProperties = { marginBottom: '1.5rem' };
const sectionTitleStyle: CSSProperties = {
  margin: '0 0 0.5rem',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'rgba(230, 237, 243, 0.5)'
};
const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse' };
const rowStyle: CSSProperties = { borderTop: '1px solid rgba(255, 255, 255, 0.06)' };
const keyCell: CSSProperties = {
  padding: '0.6rem 0.75rem 0.6rem 0',
  color: 'rgba(230, 237, 243, 0.7)',
  fontSize: '0.85rem',
  verticalAlign: 'top',
  whiteSpace: 'nowrap'
};
const valCell: CSSProperties = {
  padding: '0.6rem 0',
  fontSize: '0.85rem',
  textAlign: 'right',
  wordBreak: 'break-word'
};

const chipBase: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: '0.75rem',
  fontWeight: 700
};
const boolOnDanger: CSSProperties = { ...chipBase, background: '#c0392b', color: '#fff' };
const boolOn: CSSProperties = {
  ...chipBase,
  background: 'rgba(46, 160, 67, 0.25)',
  color: '#3fb950'
};
const boolOff: CSSProperties = {
  ...chipBase,
  background: 'rgba(255, 255, 255, 0.08)',
  color: 'rgba(230, 237, 243, 0.6)'
};
const strVal: CSSProperties = { color: '#e6edf3' };
const emptyVal: CSSProperties = { color: 'rgba(230, 237, 243, 0.35)' };
const errorStyle: CSSProperties = { color: '#ff7b72', fontSize: '0.85rem' };
const loadingStyle: CSSProperties = { color: 'rgba(230, 237, 243, 0.55)', fontSize: '0.85rem' };

export default ConfigPage;
